import _ from "lodash";
import { useCallback } from "react";

import { NUMERIC_TYPES } from "../../../../../../../../../components/QueryBuilder/constants";

import { ChartOptions } from "../../../../../../../schemas/report";

import useStore from "../../../../../store";
import { ChartOptionKey, ChartType, ReportAnalyticsRbacPermissions } from "../../../../../store/enums";

import getNested from "../../../../../../../../../utils/GetNested";
import { formatSummarizeByAndAggregationOperations } from "../../../../../utils";
import { getPointTypeFromDataseries } from "../ScatterChart/utils";

import Config from "../../../../../Config";
import OptionSettings from "../../shared/OptionSettings";

export interface Label {
	_id?: string;
	name?: string;
	type?: string;
}

const keyMap = {
	reviewReport: {
		employee: "subject",
		question: "questions_map",
		review: "review",
		reviewcycle: "review_cycle",
		reviewreport: "review_report",
		reviewform: "review_form",
		groupedquestion: "question_group_map",
	},
	surveyReport: {
		survey: "survey_report",
		groupedquestion: "question_group_map",
		question: "questions_map",
		employee: "participant",
	},
	goalReport: {
		goal: "goal",
		employee: "assignee",
	},
	employeeReport: {
		employee: "subject",
	},
};

/*
    This is a custom hook to handle get and update the chart options for each chart in the store.
	Data series is summarized columns, which is returned from getEnabledSummarizeRowsColumns() and sorts
	since by default, we want to set it to the last sorted summarize by column
*/

const useChartOptions = (chart: ChartType) => {
	const [
		visualizeOptions,
		previousChart,
		columnsSorted,
		enabledSummarizeFields,
		userPermissions,
		uiState,
		getSortFromColumnObject,
		fallbackToTableChart,
		setVisualizeOptions,
		getEnabledSummarizeRowsColumns,
		getEnabledGridColumns,
		getSummaryColumnMetadata,
		report,
	] = useStore((state) => [
		state.visualizeOptions,
		state.previousChart,
		state.sorts,
		state.enabledSummarizeFields,
		state.userPermissions,
		state.uiState,
		state._formatGridSorts,
		state.fallbackToTableChart,
		state.setVisualizeOptions,
		state.getEnabledSummarizeRowsColumns,
		state.getEnabledGridColumns,
		state.getSummaryColumnMetadata,
		state.report,
	]);

	const columnVisibilityMap = getSummaryColumnMetadata();
	const summarizedByAxis = getEnabledSummarizeRowsColumns(); // data series
	const filterAxisOptions = (axisOption) => {
		// Allow columns with count aggregation function (these are numeric metrics)
		if (axisOption.defaultAggregationFunction === "count") {
			return true;
		}
		return (
			!["multiple-choice", "multiple-select"].includes(axisOption.fieldValueType) &&
			!(axisOption.fieldType === "EMPLOYEE_FIELD" && !NUMERIC_TYPES.includes(axisOption.fieldValueType))
		);
	};

	const anyAxis = getEnabledGridColumns()
		.filter((c) => getNested(columnVisibilityMap, [c.field, "isColumnVisible"], false))
		.filter(filterAxisOptions);
	// data series in sorted order. This is used to populate the data series dropdown
	const summarizedBySorted = columnsSorted.filter((sort) => {
		const { fieldPath } = sort;
		return fieldPath && fieldPath[0] && fieldPath[0] === "summarizeBy";
	});

	// map of name to index.
	// This is used to sort the data series dropdown
	// we need both columnSorts and summarizedByAxis because columnSorts doesn't contain correct format and header name
	// and summarizedByAxis doesn't contain correct order
	const nameToIndexMap = summarizedBySorted.reduce((acc, curr, index) => {
		acc["summary_" + curr.name] = index;
		return acc;
	}, {});

	// returns options for the data series.
	const dataSeriesOptions = summarizedByAxis
		.map((data) => {
			return {
				value: data.field,
				label: data.headerName,
				type: data.fieldValueType,
			};
		})
		.sort((a, b) => {
			return nameToIndexMap[a.value] - nameToIndexMap[b.value];
		})
		.filter((option) => {
			if (chart === ChartType.TrendlineChart && uiState === "READY") {
				return option.type === "date";
			}
			return true;
		});

	const anyAxisOptions = anyAxis
		.map((data) => {
			return {
				value: data.field,
				label: data.headerName,
				metadata: data.columnMetadata,
				fixedText: " (" + _.capitalize(columnVisibilityMap[data.field].currentlySelectedSummaryFunction) + ")",
				labelWithAggregation:
					data.headerName +
					" (" +
					_.capitalize(columnVisibilityMap[data.field].currentlySelectedSummaryFunction) +
					")",
			};
		})
		.sort((a, b) => {
			return a.label.localeCompare(b.label);
		});

	// format summarize by and aggregation operations for the data series dropdown to send it to server
	const { summarizeBy: formattedSummarizeBy, aggregationOperations: formattedAggregationOperations } =
		formatSummarizeByAndAggregationOperations(enabledSummarizeFields, columnVisibilityMap);
	const _selectedDataSeries = visualizeOptions[chart]?.dataSeries?.column
		? visualizeOptions[chart]?.dataSeries?.column.split("_")
		: null;
	const selectedDataSeries = _selectedDataSeries
		? [_selectedDataSeries.shift(), _selectedDataSeries.shift(), _selectedDataSeries.join("_")]
		: null;
	const nameKeyOne = selectedDataSeries ? Config.fieldGroupLookup[selectedDataSeries[1] ?? ""] : null;
	const nameKeyTwo = selectedDataSeries ? selectedDataSeries[2] : null;

	const summarizeBySetting = formattedSummarizeBy
		.filter((s) => s.type === nameKeyOne && s.name === nameKeyTwo)
		.filter((s) => {
			if (chart === ChartType.TrendlineChart) {
				return s.fieldType === "date";
			}
			return true;
		});

	const selectDefaultXAxis = (chartType: ChartType) => {
		if (chartType === ChartType.NineBox) {
			const xAxisOption = anyAxisOptions.find((option) => option.label.toLowerCase().includes("performance"));
			return xAxisOption ? xAxisOption.value : undefined;
		}

		return undefined;
	};

	const selectDefaultYAxis = (chartType: ChartType) => {
		if (chartType === ChartType.NineBox) {
			const yAxisOption = anyAxisOptions.find((option) => option.label.toLowerCase().includes("potential"));
			return yAxisOption ? yAxisOption.value : undefined;
		}

		return undefined;
	};

	const setXAxisColumnData = (key: ChartOptionKey, value: string | number) => {
		const existingOptions = visualizeOptions[chart] || ({} as ChartOptions);
		const overwrite = key === "column" || key === "min" || key === "max";
		const resetMinMax = { hasSuggestedMin: false, hasSuggestedMax: false };
		setVisualizeOptions({
			[chart]: {
				...existingOptions,
				xAxis: { ...(existingOptions.xAxis || {}), [key]: value, ...(overwrite ? resetMinMax : {}) },
			},
		});
	};

	const setYAxisColumnData = (key: ChartOptionKey, value: string | number) => {
		const existingOptions = visualizeOptions[chart] || ({} as ChartOptions);
		const overwrite = key === "column" || key === "min" || key === "max";
		const resetMinMax = { hasSuggestedMin: false, hasSuggestedMax: false };
		setVisualizeOptions({
			[chart]: {
				...existingOptions,
				yAxis: { ...(existingOptions.yAxis || {}), [key]: value, ...(overwrite ? resetMinMax : {}) },
			},
		});
	};

	const setDataSeries = (key: ChartOptionKey, value: string | number) => {
		const existingOptions = visualizeOptions[chart] || ({} as ChartOptions);
		setVisualizeOptions({
			[chart]: { ...existingOptions, dataSeries: { ...(existingOptions.dataSeries || {}), [key]: value } },
		});
	};

	// set chart metadata for the chart, such as # bins, for column chart, since in the future, we might want a histogram with multiple data series
	const setChartMetadata = (key: ChartOptionKey, value: any) => {
		const existingOptions = visualizeOptions[chart] || {};
		setVisualizeOptions({
			[chart]: { ...existingOptions, [key]: value },
		});
	};

	// set min max x y from data returned from server if it's haven't set from review questions min max
	// if it's set from review questions min max (handled before this call), we don't want to overwrite it
	const setMinMaxXYCallback = (minX, maxX, minY, maxY) => {
		const existingOptions = JSON.parse(JSON.stringify(visualizeOptions[chart] || {}));
		const gridData = getEnabledGridColumns();

		const existingXAxis: NonNullable<ChartOptions["xAxis"]> = existingOptions?.xAxis ? existingOptions.xAxis : {};
		const existingYAxis: NonNullable<ChartOptions["yAxis"]> = existingOptions?.yAxis ? existingOptions.yAxis : {};

		const suggestedMinX = gridData.find((column) => column.field === existingXAxis?.column)?.columnMetadata?.min;
		const suggestedMaxX = gridData.find((column) => column.field === existingXAxis?.column)?.columnMetadata?.max;
		const suggestedMinY = gridData.find((column) => column.field === existingYAxis?.column)?.columnMetadata?.min;
		const suggestedMaxY = gridData.find((column) => column.field === existingYAxis?.column)?.columnMetadata?.max;

		// We need a boolean since this can come from different sources
		const hasSuggestedMinX = existingXAxis?.hasSuggestedMin;
		const hasSuggestedMaxX = existingXAxis?.hasSuggestedMax;
		const hasSuggestedMinY = existingYAxis?.hasSuggestedMin;
		const hasSuggestedMaxY = existingYAxis?.hasSuggestedMax;

		if (suggestedMinX && !hasSuggestedMinX) {
			existingXAxis.min = suggestedMinX;
			existingXAxis.hasSuggestedMin = true;
		}

		if (!suggestedMinX && !hasSuggestedMinX) {
			existingXAxis.min = minX;
			existingXAxis.hasSuggestedMin = false;
		}

		if (suggestedMaxX && !hasSuggestedMaxX) {
			existingXAxis.max = suggestedMaxX;
			existingXAxis.hasSuggestedMax = true;
		}

		if (!suggestedMaxX && !hasSuggestedMaxX) {
			existingXAxis.max = maxX;
			existingXAxis.hasSuggestedMax = false;
		}

		if (suggestedMinY && !hasSuggestedMinY) {
			existingYAxis.min = suggestedMinY;
			existingYAxis.hasSuggestedMin = true;
		}

		if (!suggestedMinY && !hasSuggestedMinY) {
			existingYAxis.min = minY;
			existingYAxis.hasSuggestedMin = false;
		}

		if (suggestedMaxY && !hasSuggestedMaxY) {
			existingYAxis.max = suggestedMaxY;
			existingYAxis.hasSuggestedMax = true;
		}

		if (!suggestedMaxY && !hasSuggestedMaxY) {
			existingYAxis.max = maxY;
			existingYAxis.hasSuggestedMax = false;
		}

		const inferredOptions = { ...existingOptions, xAxis: existingXAxis, yAxis: existingYAxis };
		let noChange = false;

		if (JSON.stringify(visualizeOptions[chart]) === JSON.stringify(inferredOptions)) {
			noChange = true;
		}
		setVisualizeOptions({ [chart]: inferredOptions }, noChange);
	};

	// format raw data to labels and datapoints for the bar chart for now
	// In progress to clean up and support other charts
	const getChartDataFromRawData = (data) => {
		const labels: Label[] = [];
		const datasets = [];
		const xColumn = visualizeOptions[chart]?.xAxis?.column;
		const yColumn = visualizeOptions[chart]?.yAxis?.column;
		const dataSeries = visualizeOptions[chart]?.dataSeries?.column.split("_");

		if (xColumn === undefined && yColumn === undefined) {
			return { labels, datasets };
		}

		// TODO: handle scatter chart and 9 box chart
		if (xColumn && yColumn) {
			return { labels, datasets };
		}

		const activeColumn = xColumn || yColumn;

		if (!activeColumn) throw new Error("No active column");

		// Given a column name, return the appropriate key to look up data
		const getColumnKey = (column: string) => {
			let key = column.split("_")?.[0];
			if (key === "review") {
				if (column.startsWith("review_cycle")) {
					key = "review_cycle";
				} else if (column.startsWith("review_report")) {
					key = "review_report";
				}
			}
			// Note: For survey fields, we keep key as "survey" (not "survey_report")
			// because column IDs are "survey_fieldname", not "survey_report_fieldname"

			return key;
		};

		const theKey = getColumnKey(activeColumn);
		// For survey reports, use survey_report; for review reports, use review_report
		const isSurveyReport = report?.type === "surveyReport";
		const reportDataKey =
			isSurveyReport && theKey === "survey"
				? "survey_report"
				: keyMap?.[report?.type || "reviewReport"]?.[theKey];

		const reportObjectKey = isSurveyReport ? "survey_report" : "review_report";

		// handle bar chart
		if (data && data.reviewReportsVisualization && nameKeyOne && activeColumn) {
			data.reviewReportsVisualization.forEach((row) => {
				// bring custom_values 1 level up for formatting purposes
				if (row["subject"] && row["subject"]["custom_values"]) {
					row["subject"] = { ...row["subject"], ...row["subject"]["custom_values"] };
				}
				if (row["assignee"] && row["assignee"]["custom_values"]) {
					row["assignee"] = { ...row["assignee"], ...row["assignee"]["custom_values"] };
				}
				if (row["participant"] && row["participant"]["custom_values"]) {
					row["participant"] = { ...row["participant"], ...row["participant"]["custom_values"] };
				}
				// Get value from data returned by server based on key
				const getXValue = (key: string, data: any, columnId: string = "") => {
					// handle authors and signers count data - these live in report object not review_form
					if ("reviewform_authors" === columnId) {
						return row[reportObjectKey]["authors"];
					}
					if ("reviewform_signers" === columnId) {
						return row[reportObjectKey]["signers"];
					}

					// handle key and data type
					// Check if key exists in the appropriate report type's keyMap
					const reportType = report?.type || "reviewReport";
					if (key in (keyMap[reportType] || {}) && typeof data === "object" && data !== null) {
						return data?.answer;
					}
					return data;
				};
				const label = row.summarizeBy[nameKeyOne]?.[nameKeyTwo || ""] || "";
				const fieldName = activeColumn.replace(`${theKey}_`, "");

				let xData = row[reportDataKey]?.[fieldName] || row?.[fieldName];
				xData = getXValue(theKey, xData, activeColumn);
				const pointType = getPointTypeFromDataseries(dataSeries);
				label.type = pointType;
				if (pointType === "employee") {
					labels.push(label);
					datasets.push(xData as (typeof datasets)[number]);
				} else {
					// if it's not a subject, we can remove the _id for the avatar
					if (label && label._id) {
						delete label._id;
					}
					if (label) {
						labels.push(label);
						datasets.push(xData as (typeof datasets)[number]);
					}
				}
			});
		}

		// get top or bottom N rows. Assuming max N is 25.
		const rangeOption = visualizeOptions[chart]?.dataSeries?.rangeOption;
		// if range option doesn't exist or it's month for timeline chart, we don't want to limit the data
		// if it exists and not month, we want to limit the data to top N rows
		const sortLimit =
			rangeOption && !rangeOption.includes("month") && !rangeOption.includes("quarter")
				? parseInt(rangeOption.split("_")[1] || 25)
				: 100;
		return { labels: labels.slice(0, sortLimit), datasets: datasets.slice(0, sortLimit) };
	};

	const getSortSettings = useCallback(() => {
		const sortRange = visualizeOptions[chart]?.dataSeries?.rangeOption;
		const xColumn = visualizeOptions[chart]?.xAxis?.column;
		const yColumn = visualizeOptions[chart]?.yAxis?.column;
		const selectedColumnMetaDataY = anyAxis.filter((c) => c.field === yColumn)[0] || null;
		const selectedColumnMetaDataX = anyAxis.filter((c) => c.field === xColumn)[0] || null;
		const sortDirection = sortRange ? (sortRange.split("_")[0] === "TOP" ? "desc" : "asc") : "desc";

		// Edge case: overwriting the field path for review subject since there's some special handling for it
		const fieldPathX = _.isEqual(selectedColumnMetaDataX?.fieldPath, ["review", "subject"])
			? ["subject", "name"]
			: selectedColumnMetaDataX?.fieldPath;
		const fieldPathY = _.isEqual(selectedColumnMetaDataY?.fieldPath, ["review", "subject"])
			? ["subject", "name"]
			: selectedColumnMetaDataY?.fieldPath;

		// get secondary sort by data series
		const secondarySort = visualizeOptions[chart]?.dataSeries?.column
			? getSortFromColumnObject([
					{
						colId: visualizeOptions[chart]?.dataSeries?.column,
						direction: "asc",
					},
				])?.[0]
			: null;

		const sorts = [
			...(selectedColumnMetaDataY
				? [
						{
							name: selectedColumnMetaDataY?.headerName,
							fieldPath: fieldPathY,
							direction: sortDirection,
							type: selectedColumnMetaDataY.fieldType,
						},
					]
				: []),
			...(selectedColumnMetaDataX
				? [
						{
							name: selectedColumnMetaDataX?.headerName,
							fieldPath: fieldPathX,
							direction: sortDirection,
							type: selectedColumnMetaDataX.fieldType,
						},
					]
				: []),
			...(secondarySort ? [secondarySort] : []),
		];
		// Deduplicate sorts by fieldPath - MongoDB cannot handle duplicate array field sorts
		return _.uniqBy(sorts, (sort) => JSON.stringify(sort.fieldPath));
	}, [
		visualizeOptions[chart]?.dataSeries?.rangeOption,
		visualizeOptions[chart]?.xAxis?.column,
		visualizeOptions[chart]?.yAxis?.column,
	]);

	// get all the selectable sort settings for the chart, to use in prefetching data
	const getAllSelectableSortSettings = useCallback(() => {
		const selectableOptions =
			OptionSettings[chart]?.yAxisRangeOptions ||
			OptionSettings[chart]?.xAxisRangeOptions ||
			OptionSettings[chart]?.dataSeriesRangeOptions ||
			[];
		const allSelectableSortSettings = !userPermissions.includes(ReportAnalyticsRbacPermissions.CreateReport)
			? []
			: selectableOptions.map((rangeOption) => {
					const settings = getSortSettings();
					const sortDirection = rangeOption.value.split("_")[0] === "TOP" ? "desc" : "asc";
					return [
						{
							name: settings[0]?.name,
							fieldPath: settings[0]?.fieldPath,
							direction: sortDirection,
							type: settings[0]?.type,
						},
						...(settings.length > 1 ? settings.slice(1) : []),
					];
				});
		return allSelectableSortSettings;
	}, [
		visualizeOptions[chart]?.dataSeries?.rangeOption,
		visualizeOptions[chart]?.xAxis?.column,
		visualizeOptions[chart]?.yAxis?.column,
	]);

	const getAxisOptionsAsColumns = useCallback(() => {
		const xColumn = visualizeOptions[chart]?.xAxis?.column;
		const yColumn = visualizeOptions[chart]?.yAxis?.column;
		const selectedColumnMetaDataY = anyAxis.filter((c) => c.field === yColumn)[0] || null;
		const selectedColumnMetaDataX = anyAxis.filter((c) => c.field === xColumn)[0] || null;
		const columns = [
			...(xColumn
				? [
						{
							id: xColumn,
							type: selectedColumnMetaDataX?.fieldType,
						},
					]
				: []),
			...(yColumn
				? [
						{
							id: yColumn,
							type: selectedColumnMetaDataY?.fieldType,
						},
					]
				: []),
		];
		// Deduplicate columns by id - MongoDB cannot handle duplicate array field columns
		return _.uniqBy(columns, "id");
	}, [visualizeOptions[chart]?.xAxis?.column, visualizeOptions[chart]?.yAxis?.column]);

	return {
		fallbackToTableChart,
		selectDefaultXAxis,
		selectDefaultYAxis,
		setXAxisColumnData,
		setYAxisColumnData,
		setDataSeries,
		setChartMetadata,
		setVisualizeOptions,
		setMinMaxXYCallback,
		getChartDataFromRawData,
		sortSettings: getSortSettings(),
		getSortSettings: getSortSettings,
		selectableSortSettings: getAllSelectableSortSettings(),
		getAxisOptionsAsColumns,
		summarizeBySetting,
		aggregationOperations: formattedAggregationOperations,
		visualizeOption: visualizeOptions[chart] || ({} as ChartOptions),
		previousVisualizeOption: visualizeOptions[previousChart] || {},
		dataSeriesOptions,
		anyAxisOptions,
		previousChart,
	};
};

export default useChartOptions;
